import { useMemo, useState } from 'react';

import { dateFormat } from '@i18n';
import { Lock } from '@mui/icons-material';
import { Box, Button, SxProps, Tooltip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAccount, useClient } from 'wagmi';

import {
  useReadRouterWorkerRegistration,
  useReadWorkerRegistryLockPeriod,
  workerRegistryAbi,
} from '@api/contracts';
import { useWriteSQDTransaction } from '@api/contracts/useWriteTransaction';
import { AccountType, SourceWallet, Worker } from '@api/subsquid-network-squid';
import { trpc } from '@api/trpc';
import { ContractCallDialog } from '@components/ContractCallDialog';
import { useContracts } from '@hooks/network/useContracts';
import { useCountdown } from '@hooks/useCountdown';
import { useSquidHeight } from '@hooks/useSquidNetworkHeightHooks';
import { peerIdToHex } from '@lib/network';

function UnlocksTooltip({ timestamp }: { timestamp?: number }) {
  const timeLeft = useCountdown({ timestamp });

  return <span>{`Unlocks in ${timeLeft} (${dateFormat(timestamp, 'dateTime')})`}</span>;
}

export function WorkerWithdrawButton({
  worker,
  source,
  disabled,
  sx,
}: {
  sx?: SxProps;
  worker: Pick<Worker, 'id' | 'status' | 'peerId'> & {
    statusHistory?: { blockNumber: number }[];
  };
  source: SourceWallet & {
    locked: boolean;
    lockEnd?: number | null;
  };
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const { data: currentEpoch } = useQuery(
    trpc.network.currentEpoch.queryOptions(undefined, { refetchInterval: 12_000 }),
  );

  const contracts = useContracts();
  const { data: registrationAddress } = useReadRouterWorkerRegistration({
    address: contracts.ROUTER,
  });
  const { data: lockPeriod } = useReadWorkerRegistryLockPeriod({
    address: registrationAddress,
  });

  // The indexer fixes lockEnd at deregistration, while the contract checks
  // deregisteredAt + the current lockPeriod(), which can be changed afterwards.
  const lockEnd = useMemo(() => {
    const history = worker.statusHistory;
    const deregisteredAt = history?.length ? history[history.length - 1].blockNumber : undefined;
    if (lockPeriod === undefined || deregisteredAt === undefined) return source.lockEnd;

    return deregisteredAt + Number(lockPeriod);
  }, [worker.statusHistory, lockPeriod, source.lockEnd]);

  const locked = useMemo(() => {
    if (!currentEpoch || !lockEnd) return source.locked;

    return lockEnd > currentEpoch.lastBlockL1;
  }, [currentEpoch, lockEnd, source.locked]);

  const unlockTimestamp = useMemo(() => {
    if (!currentEpoch || !lockEnd) return;

    return (
      (lockEnd - currentEpoch.lastBlockL1 + 1) * currentEpoch.blockTimeL1 +
      new Date(currentEpoch.lastBlockTimestampL1).getTime()
    );
  }, [currentEpoch, lockEnd]);

  return (
    <>
      <Tooltip
        title={
          !disabled && locked && unlockTimestamp && <UnlocksTooltip timestamp={unlockTimestamp} />
        }
        placement="top"
      >
        <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
          <Button
            loading={open}
            onClick={() => setOpen(true)}
            variant="outlined"
            color="error"
            disabled={disabled || locked}
          >
            WITHDRAW
            {locked && !disabled && (
              <Lock
                fontSize="small"
                sx={{
                  color: 'action.active',
                  position: 'absolute',
                  top: '0px',
                  right: '0px',
                  transform: 'translate(0%, -25%)',
                  zIndex: 1,
                }}
              />
            )}
          </Button>
        </Box>
      </Tooltip>
      <WorkerWithdrawDialog
        open={open}
        onClose={() => setOpen(false)}
        worker={worker}
        source={source}
      />
    </>
  );
}

export function WorkerWithdrawDialog({
  open,
  onClose,
  worker,
  source,
}: {
  open: boolean;
  onClose: () => void;
  worker: Pick<Worker, 'id' | 'status' | 'peerId'>;
  source: SourceWallet;
}) {
  const client = useClient();
  const account = useAccount();
  const { setWaitHeight } = useSquidHeight();

  const contracts = useContracts();
  const contractWriter = useWriteSQDTransaction();

  const { data: registrationAddress } = useReadRouterWorkerRegistration({
    address: contracts.ROUTER,
  });

  const handleSubmit = async () => {
    if (!client) return;
    if (!account.address || !registrationAddress) return;

    const peerIdHex = peerIdToHex(worker.peerId);

    const receipt = await contractWriter.writeTransactionAsync({
      address: registrationAddress,
      abi: workerRegistryAbi,
      functionName: 'withdraw',
      args: [peerIdHex],
      vesting: source.type === AccountType.User ? undefined : (source.id as `0x${string}`),
    });
    setWaitHeight(receipt.blockNumber, []);

    onClose();
  };

  return (
    <ContractCallDialog
      title="Withdraw worker?"
      open={open}
      onResult={confirmed => {
        if (!confirmed) return onClose();

        handleSubmit();
      }}
      loading={contractWriter.isPending}
      hideCancelButton={false}
    >
      Are you sure you want to withdraw your tokens from this worker?
    </ContractCallDialog>
  );
}
